import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import Container from "@/components/containers/Container";
import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import { useHasPermission } from "@/hooks/useHasPermission";
import type { ChildRouteContext } from "@/root";
import { throwIfHasNoPermission } from "@/utils/permissions";

import UnitLevelList from "@/modules/chart/admin/components/unit-level/UnitLevelList";
import UnitList from "@/modules/chart/admin/components/unit/UnitList";
import { fetchUnitsQueryOptions } from "@/modules/chart/admin/services";
import { ChartSelector } from "@/modules/chart/shared/components/ChartSelector";

export const Route = createFileRoute("/admin/_panel/_chart/chart/$type/units/")(
  {
    beforeLoad: ({ context }) => {
      throwIfHasNoPermission(context.user, [
        "chart.chart-unit.view",
        "chart.chart-unit-level.view",
      ]);

      return {
        crumb: [
          {
            label: context.t("Units", { ns: "chart.admin" }),
          },
        ],
      } satisfies ChildRouteContext;
    },
    loader: (opts) =>
      opts.context.queryClient.prefetchQuery(fetchUnitsQueryOptions()),
    component: Component,
  },
);

function Component() {
  const { t } = useTranslation("chart.admin");

  const { type } = Route.useParams();
  const navigate = Route.useNavigate();

  const hasPermission = useHasPermission();

  return (
    <Container
      title={t("Manage Units and Levels")}
      description={t("Manage, create, or edit organizational units")}
      action={
        <div className="lg:w-96">
          <ChartSelector
            value={type}
            onChange={(type) =>
              navigate({
                to: Route.fullPath,
                params: {
                  type: type,
                },
              })
            }
          />
        </div>
      }
    >
      <TabGroup>
        <TabList>
          {hasPermission("chart.chart-unit.view") && <Tab>{t("Units")}</Tab>}
          {hasPermission("chart.chart-unit-level.view") && (
            <Tab>{t("Unit Levels")}</Tab>
          )}
        </TabList>
        <TabPanels>
          {hasPermission("chart.chart-unit.view") && (
            <TabPanel>
              <UnitList type={type} />
            </TabPanel>
          )}
          {hasPermission("chart.chart-unit-level.view") && (
            <TabPanel>
              <UnitLevelList type={type} />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>
    </Container>
  );
}
